<template>
    <div class="keys-page" v-reveal="{ anim: 'unfold', stagger: 90, y: 28 }">
        <!-- ====== 顶部统计指标 ====== -->
        <section class="stats-grid" v-reveal="{ anim: 'pop', stagger: 80, y: 22 }">
            <StatCard label="卡密总数" :value="rows.length" icon="🔑" accent="violet" hint="数据库内全部卡密" />
            <StatCard label="当前在线" :value="onlineCount" icon="📡" accent="mint" hint="正在使用中的卡密" />
            <StatCard label="多开实例" :value="multiCount" icon="⚡" accent="amber" hint="同卡密多个会话" />
            <StatCard label="已禁用" :value="disabledCount" icon="⛔" accent="pink" hint="禁用 / 封禁 / 失效" />
        </section>

        <!-- ====== 工具栏 ====== -->
        <section class="toolbar card neon">
            <div class="tb-left">
                <div class="search-box">
                    <span class="search-ico">🔍</span>
                    <input class="input search" v-model="q" placeholder="搜索卡密或备注…" />
                </div>
                <label class="check">
                    <input type="checkbox" v-model="onlyOnline" />
                    <span>只看在线</span>
                </label>
            </div>

            <div class="tb-right">
                <span class="badge">显示 <b>{{ filtered.length }}</b> / {{ rows.length }}</span>

                <!-- 视图切换：卡片为主，表格为辅 -->
                <div class="view-switch">
                    <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="卡片视图">▦</button>
                    <button :class="{ on: view === 'table' }" @click="view = 'table'" title="表格视图">≣</button>
                </div>

                <button class="btn" @click="refreshAll">刷新</button>
                <button class="btn btn-primary" @click="genOpen = !genOpen">
                    {{ genOpen ? '收起生成' : '生成卡密' }}
                </button>
            </div>
        </section>

        <!-- ====== 生成卡密：可折叠面板 ====== -->
        <transition name="collapse">
            <section v-if="genOpen" class="gen card neon glow">
                <div class="gen-grid">
                    <div class="field">
                        <div class="cap">数量</div>
                        <input class="input" v-model.number="gen.count" type="number" min="1" max="500" />
                    </div>
                    <div class="field">
                        <div class="cap">卡密类型</div>
                        <select class="input" v-model="gen.mode">
                            <option value="expire_at">按天卡</option>
                            <option value="duration">时长卡</option>
                        </select>
                    </div>
                    <div class="field" v-if="gen.mode === 'expire_at'">
                        <div class="cap">天数</div>
                        <input class="input" v-model="gen.days" placeholder="例如 30" />
                    </div>
                    <div class="field" v-else>
                        <div class="cap">小时数</div>
                        <input class="input" v-model="gen.hours" placeholder="例如 72" />
                    </div>
                    <div class="field grow">
                        <div class="cap">备注</div>
                        <input class="input" v-model="gen.note" placeholder="备注（可空）" />
                    </div>
                    <div class="field gen-actions">
                        <button class="btn btn-primary" @click="doGenerate">立即生成</button>
                    </div>
                </div>

                <!-- 生成结果区 -->
                <div v-if="genResult.length" class="gen-result">
                    <div class="gr-head">
                        <span class="cap">本次生成（{{ genResult.length }}）</span>
                        <div class="gr-actions">
                            <button class="btn btn-mini" @click="copyGenerated">复制结果</button>
                            <button class="btn btn-mini" @click="downloadGenerated">下载文本</button>
                        </div>
                    </div>
                    <pre class="pre mono">{{ genResultText }}</pre>
                </div>
            </section>
        </transition>

        <!-- ====== 主体：卡片网格 / 表格 ====== -->
        <section class="body">
            <!-- 卡片网格视图（主） -->
            <div v-if="view === 'grid'" class="cards">
                <TiltCard v-for="(r, i) in filtered" :key="r.key" :max="7" :disabled="isTouch" class="key-card">
                    <div class="card-in" :style="{ animationDelay: Math.min(i * 0.045, 0.7) + 's' }">
                        <!-- 头部：状态条 + 类型 -->
                        <div class="kc-top">
                            <span class="pill" :class="pillClass(r)">
                                <span class="dot" :class="{ live: r.online }"></span>
                                {{ statusText(r.status) }}
                            </span>
                            <span class="pill" :class="Number(r.billing_mode) === 2 ? 'pill-good' : 'pill-ok'">
                                {{ Number(r.billing_mode) === 2 ? '时长卡' : '按天卡' }}
                            </span>
                        </div>

                        <!-- 卡密文本 -->
                        <div class="kc-key">
                            <span class="kc-key-text mono" :title="r.key">{{ r.key }}</span>
                            <button class="ico-btn" title="复制卡密" @click="copyKey(r.key)">⧉</button>
                        </div>
                        <div v-if="r.disabled_reason" class="kc-reason warn">⚠ {{ r.disabled_reason }}</div>

                        <!-- 时间 / 时长 -->
                        <div class="kc-time">
                            <template v-if="Number(r.billing_mode) === 2">
                                <div class="kc-time-main">剩余 {{ fmtDurationShort(r.remaining_seconds) }}</div>
                                <div class="kc-time-sub muted2">
                                    总 {{ fmtDurationShort(r.total_seconds) }} · 已用 {{ fmtDurationShort(r.used_seconds) }}
                                </div>
                            </template>
                            <template v-else>
                                <div class="kc-time-main">{{ fmtDate(r.expires_at) }}</div>
                                <div class="kc-time-sub muted2" v-if="r.expires_at">{{ expiresHint(r.expires_at) }}</div>
                            </template>
                        </div>

                        <!-- 在线信息 + 备注 -->
                        <div class="kc-meta">
                            <span class="meta-chip" :class="r.online ? 'good' : 'muted2'">
                                {{ r.online ? '在线' : '离线' }} · {{ Number(r.active_sessions || 0) }} 开
                            </span>
                            <span class="meta-chip muted2" v-if="r.last_heartbeat_at">
                                心跳 {{ fmtTimeOnly(r.last_heartbeat_at) }}
                            </span>
                        </div>
                        <div class="kc-note muted" v-if="r.note">📝 {{ r.note }}</div>

                        <hr class="sep kc-sep" />

                        <!-- 操作区 -->
                        <div class="kc-actions">
                            <div class="extend-row">
                                <input class="input input-mini" v-model="extendValue[r.key]"
                                    :placeholder="Number(r.billing_mode) === 2 ? '小时' : '天数'" />
                                <button class="btn btn-mini" @click="extend(r)">
                                    {{ Number(r.billing_mode) === 2 ? '加时长' : '延天数' }}
                                </button>
                            </div>
                            <div class="op-row">
                                <button class="btn btn-mini" @click="enable(r.key)">启用</button>
                                <button class="btn btn-mini btn-danger" @click="openDisable(r.key)">禁用</button>
                                <button class="btn btn-mini btn-danger" @click="openDelete(r.key)">删除</button>
                            </div>
                        </div>
                    </div>
                </TiltCard>
            </div>

            <!-- 表格视图（辅） -->
            <div v-else class="table-card card neon">
                <div class="table-scroll">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>卡密</th>
                                <th>状态</th>
                                <th>类型</th>
                                <th>时长 / 到期</th>
                                <th>在线</th>
                                <th>备注</th>
                                <th class="ta-right">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in filtered" :key="r.key" class="row-hover">
                                <td class="mono">
                                    <div class="cell-key">
                                        <span class="ellipsis" :title="r.key">{{ r.key }}</span>
                                        <button class="ico-btn" @click="copyKey(r.key)" title="复制">⧉</button>
                                    </div>
                                    <div v-if="r.disabled_reason" class="warn small">{{ r.disabled_reason }}</div>
                                </td>
                                <td>
                                    <span class="pill" :class="pillClass(r)">{{ statusText(r.status) }}</span>
                                </td>
                                <td>
                                    <span class="pill" :class="Number(r.billing_mode) === 2 ? 'pill-good' : 'pill-ok'">
                                        {{ Number(r.billing_mode) === 2 ? '时长卡' : '按天卡' }}
                                    </span>
                                </td>
                                <td>
                                    <template v-if="Number(r.billing_mode) === 2">
                                        <div>剩余 {{ fmtDurationShort(r.remaining_seconds) }}</div>
                                        <div class="small muted2">总 {{ fmtDurationShort(r.total_seconds) }}</div>
                                    </template>
                                    <template v-else>
                                        <div>{{ fmtDate(r.expires_at) }}</div>
                                        <div class="small muted2" v-if="r.expires_at">{{ expiresHint(r.expires_at) }}</div>
                                    </template>
                                </td>
                                <td>
                                    <div :class="r.online ? 'good' : 'muted2'">
                                        {{ r.online ? '在线' : '离线' }} · {{ Number(r.active_sessions || 0) }}开
                                    </div>
                                </td>
                                <td>
                                    <div class="ellipsis" :title="r.note || ''" style="max-width:200px">{{ r.note || '-' }}</div>
                                </td>
                                <td>
                                    <div class="t-ops">
                                        <input class="input input-mini" v-model="extendValue[r.key]"
                                            :placeholder="Number(r.billing_mode) === 2 ? '小时' : '天'" />
                                        <button class="btn btn-mini" @click="extend(r)">延期</button>
                                        <button class="btn btn-mini" @click="enable(r.key)">启用</button>
                                        <button class="btn btn-mini btn-danger" @click="openDisable(r.key)">禁用</button>
                                        <button class="btn btn-mini btn-danger" @click="openDelete(r.key)">删除</button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <EmptyState v-if="!filtered.length" title="没有匹配的卡密" desc="试试调整搜索关键字或关闭“只看在线”筛选。" />
        </section>

        <!-- ====== 弹窗 ====== -->
        <CyberModal :open="modal.disable.open" title="禁用卡密" :message="modal.disable.key" ok-text="确认禁用"
            @close="modal.disable.open = false" @cancel="modal.disable.open = false" @ok="doDisableConfirm">
            <div class="cap">禁用原因</div>
            <input class="input" v-model="modal.disable.reason" placeholder="请输入禁用原因" />
        </CyberModal>

        <CyberModal :open="modal.delete.open" title="删除卡密（不可恢复）" :message="modal.delete.key" ok-text="确认删除"
            @close="modal.delete.open = false" @cancel="modal.delete.open = false" @ok="doDeleteConfirm">
            <div class="warn-line">
                <b class="bad">注意</b>
                <span class="muted">删除后不可恢复，建议先禁用再删除。</span>
            </div>
        </CyberModal>
    </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref } from "vue";
import { keysApi } from "../api/keys";

import StatCard from "../components/StatCard.vue";
import TiltCard from "../components/TiltCard.vue";
import EmptyState from "../components/EmptyState.vue";
import CyberModal from "../components/CyberModal.vue";

// 全局 toast（挂在 $root 上，由 App.vue 提供）
import { getCurrentInstance } from "vue";
const inst = getCurrentInstance();
function notify(type, title, message) {
    inst?.root?.proxy?.$toast?.push?.({ type, title, message });
}

const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

// ---- 状态 ----
const rows = ref([]);          // keys/list 结果
const onlineRows = ref([]);    // keys/online 结果
const q = ref("");
const onlyOnline = ref(false);
const view = ref("grid");      // grid | table
const genOpen = ref(false);
const extendValue = reactive({});

const gen = reactive({
    count: 1,
    mode: "expire_at",
    days: "",
    hours: "",
    note: "",
});
const genResult = ref([]);

const modal = reactive({
    disable: { open: false, key: "", reason: "DISABLED_BY_ADMIN" },
    delete: { open: false, key: "" },
});

let onlineTimer = null;

// ---- 工具函数 ----
function statusText(s) {
    if (s === 1) return "启用";
    if (s === 2) return "禁用";
    if (s === 3) return "封禁";
    if (s === 4) return "失效";
    return String(s);
}

function fmtDate(v) {
    if (!v) return "永久";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function fmtTimeOnly(v) {
    if (!v) return "-";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleTimeString();
}

function fmtDurationShort(sec) {
    const s = Math.max(0, Number(sec || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const rs = s % 60;
    if (h > 0) return `${h}小时 ${m}分`;
    if (m > 0) return `${m}分 ${rs}秒`;
    return `${rs}秒`;
}

function expiresHint(v) {
    try {
        const t = new Date(v).getTime();
        if (Number.isNaN(t)) return "";
        const days = Math.ceil((t - Date.now()) / 86400000);
        if (days > 0) return `剩余 ${days} 天`;
        if (days === 0) return "今天到期";
        return `已过期 ${Math.abs(days)} 天`;
    } catch {
        return "";
    }
}

function pillClass(r) {
    if (r.status === 2 || r.status === 3 || r.status === 4) return "pill-bad";
    if (r.status === 1) return r.online ? "pill-good" : "pill-ok";
    return "pill-ok";
}

// ---- 数据加载 ----
async function refresh() {
    try {
        const out = await keysApi.list();
        if (!out?.ok) {
            notify("bad", "列表加载失败", out?.code || "LIST_FAILED");
            return;
        }
        rows.value = out.rows || [];
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function refreshOnline() {
    try {
        const out = await keysApi.online();
        if (!out?.ok) return; // 在线数据失败不打扰用户，静默
        onlineRows.value = out.rows || [];
    } catch {
        // 轮询失败忽略
    }
}

async function refreshAll() {
    await refresh();
    await refreshOnline();
    notify("ok", "已刷新", `共 ${rows.value.length} 张卡密`);
}

// list 与 online 两份数据按 key 合并，online 字段覆盖 list
function mergedRows() {
    const map = new Map();
    for (const r of rows.value || []) map.set(r.key, { ...r });
    for (const o of onlineRows.value || []) {
        map.set(o.key, { ...(map.get(o.key) || { key: o.key }), ...o });
    }
    return Array.from(map.values());
}

const filtered = computed(() => {
    const kw = q.value.trim().toLowerCase();
    let arr = mergedRows();
    if (kw) {
        arr = arr.filter(
            (r) =>
                String(r.key || "").toLowerCase().includes(kw) ||
                String(r.note || "").toLowerCase().includes(kw)
        );
    }
    if (onlyOnline.value) arr = arr.filter((r) => r.online === true);
    return arr;
});

// 顶部统计
const onlineCount = computed(() => mergedRows().filter((r) => r.online === true).length);
const multiCount = computed(() => mergedRows().filter((r) => Number(r.active_sessions || 0) > 1).length);
const disabledCount = computed(() => rows.value.filter((r) => r.status !== 1).length);

// ---- 生成 ----
async function doGenerate() {
    try {
        // 按类型只带对应字段，保持与后端契约一致
        const payload =
            gen.mode === "duration"
                ? {
                    count: gen.count,
                    mode: "duration",
                    hours: gen.hours === "" ? null : Number(gen.hours),
                    note: gen.note || null,
                }
                : {
                    count: gen.count,
                    mode: "expire_at",
                    days: gen.days === "" ? null : Number(gen.days),
                    note: gen.note || null,
                };

        const out = await keysApi.generate(payload);
        if (!out?.ok) {
            notify("bad", "生成失败", out?.code || "GENERATE_FAILED");
            return;
        }
        genResult.value = out.keys || [];
        notify("ok", "生成成功", `本次共生成 ${genResult.value.length} 张卡密`);
        await refreshAll();
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

const genResultText = computed(() =>
    (genResult.value || [])
        .map((x) =>
            x.mode === "duration"
                ? `${x.key}\t时长卡\t${fmtDurationShort(x.total_seconds)}\t${x.note || ""}`
                : `${x.key}\t按天卡\t${x.expires_at ? new Date(x.expires_at).toISOString() : "永久"}\t${x.note || ""}`
        )
        .join("\n")
);

function copyGenerated() {
    navigator.clipboard.writeText(genResultText.value);
    notify("ok", "已复制", `共 ${genResult.value.length} 行`);
}

function downloadGenerated() {
    const blob = new Blob([genResultText.value || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `keys_${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    notify("ok", "下载成功", "文本文件已生成");
}

// ---- 单卡操作 ----
function openDisable(key) {
    modal.disable.key = key;
    modal.disable.reason = "DISABLED_BY_ADMIN";
    modal.disable.open = true;
}

async function doDisableConfirm() {
    const key = modal.disable.key;
    const reason = (modal.disable.reason || "DISABLED_BY_ADMIN").trim();
    modal.disable.open = false;
    try {
        const out = await keysApi.disable({ key, reason });
        if (!out?.ok) return notify("bad", "禁用失败", out?.code || "DISABLE_FAILED");
        notify("ok", "已禁用", key);
        await refreshAll();
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function enable(key) {
    try {
        const out = await keysApi.enable({ key });
        if (!out?.ok) return notify("bad", "启用失败", out?.code || "ENABLE_FAILED");
        notify("ok", "已启用", key);
        await refreshAll();
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

function openDelete(key) {
    modal.delete.key = key;
    modal.delete.open = true;
}

async function doDeleteConfirm() {
    const key = modal.delete.key;
    modal.delete.open = false;
    try {
        const out = await keysApi.deleteKey({ key });
        if (!out?.ok) return notify("bad", "删除失败", out?.code || "DELETE_FAILED");
        notify("ok", "已删除", key);
        await refreshAll();
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function extend(row) {
    try {
        const raw = String(extendValue[row.key] || "").trim();
        const n = Number(raw);
        if (!n || n <= 0) return notify("warn", "输入无效", "请输入大于 0 的数值");

        // 时长卡按小时延期，按天卡按天延期
        const out =
            Number(row.billing_mode) === 2
                ? await keysApi.extend({ key: row.key, hours: n })
                : await keysApi.extend({ key: row.key, days: n });

        if (!out?.ok) return notify("bad", "延期失败", out?.code || "EXTEND_FAILED");
        extendValue[row.key] = "";
        notify("ok", "操作成功",
            Number(row.billing_mode) === 2 ? `${row.key} 增加 ${n} 小时` : `${row.key} 延长 ${n} 天`);
        await refreshAll();
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

function copyKey(k) {
    navigator.clipboard.writeText(k);
    notify("ok", "复制成功", k);
}

// ---- 轮询：每 10 秒刷新在线状态 ----
onMounted(async () => {
    await refresh();
    await refreshOnline();
    onlineTimer = setInterval(refreshOnline, 10000);
});

onBeforeUnmount(() => {
    if (onlineTimer) clearInterval(onlineTimer);
});
</script>

<style scoped>
.keys-page {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* 统计卡网格 */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

/* 工具栏 */
.toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    flex-wrap: wrap;
}

.tb-left,
.tb-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.search-box {
    position: relative;
}

.search-ico {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    opacity: .6;
}

.search {
    width: 280px;
    max-width: 100%;
    padding-left: 34px;
}

.check {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--muted);
    cursor: pointer;
    user-select: none;
}

.view-switch {
    display: inline-flex;
    border: 1px solid var(--stroke);
    border-radius: 10px;
    overflow: hidden;
}

.view-switch button {
    width: 38px;
    height: 38px;
    background: transparent;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 16px;
}

.view-switch button.on {
    background: var(--grad-violet-cyan);
    color: #fff;
}

/* 生成面板 */
.gen {
    padding: 16px;
}

.gen-grid {
    display: grid;
    grid-template-columns: 90px 130px 130px 1fr auto;
    gap: 12px;
    align-items: end;
}

.gen-grid .grow {
    min-width: 160px;
}

.gen-actions {
    display: flex;
    align-items: flex-end;
}

.gen-result {
    margin-top: 14px;
}

.gr-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.gr-actions {
    display: flex;
    gap: 8px;
}

.pre {
    background: rgba(0, 0, 0, .3);
    border: 1px solid var(--stroke);
    border-radius: var(--r-sm);
    padding: 12px;
    margin: 0;
    max-height: 220px;
    overflow: auto;
    font-size: 12px;
    white-space: pre;
}

/* 卡片网格 */
.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
}

.key-card {
    padding: 16px;
}

.kc-top {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.kc-key {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kc-key-text {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.kc-reason {
    margin-top: 6px;
    font-size: 12px;
}

.kc-time {
    margin-top: 12px;
}

.kc-time-main {
    font-size: 15px;
    font-weight: 700;
}

.kc-time-sub {
    margin-top: 3px;
    font-size: 12px;
}

.kc-meta {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.meta-chip {
    font-size: 12px;
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, .04);
    border: 1px solid var(--stroke-soft);
}

.kc-note {
    margin-top: 10px;
    font-size: 12.5px;
    line-height: 1.4;
    word-break: break-word;
}

.kc-sep {
    margin: 14px 0 12px;
}

.kc-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.extend-row,
.op-row {
    display: flex;
    gap: 8px;
}

.extend-row {
    align-items: center;
}

.input-mini {
    width: 84px;
    padding: 7px 10px;
    border-radius: 10px;
}

.op-row {
    flex-wrap: wrap;
}

.op-row .btn {
    flex: 1 1 auto;
}

.ico-btn {
    flex: 0 0 auto;
    border: 1px solid var(--stroke);
    background: rgba(255, 255, 255, .05);
    color: var(--muted);
    border-radius: 9px;
    padding: 5px 9px;
    cursor: pointer;
    transition: all var(--t);
}

.ico-btn:hover {
    border-color: rgba(0, 229, 255, .5);
    color: #fff;
}

/* 表格视图 */
.table-card {
    padding: 6px;
}

.table-scroll {
    overflow: auto;
    border-radius: var(--r-md);
    max-height: 70vh;
}

.cell-key {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
}

.ta-right {
    text-align: right;
}

.small {
    font-size: 12px;
}

.t-ops {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.warn-line {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border-radius: var(--r-sm);
    border: 1px solid rgba(255, 176, 32, .3);
    background: rgba(255, 176, 32, .08);
}

/* 折叠动画 */
.collapse-enter-active,
.collapse-leave-active {
    transition: opacity var(--t), transform var(--t);
}

.collapse-enter-from,
.collapse-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* ---------------- 响应式 ---------------- */
@media (max-width: 1100px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .gen-grid {
        grid-template-columns: 1fr 1fr;
    }

    .gen-grid .grow,
    .gen-actions {
        grid-column: 1 / -1;
    }
}

@media (max-width: 768px) {
    .tb-left,
    .tb-right {
        width: 100%;
        justify-content: space-between;
    }

    .search {
        width: 100%;
    }

    .cards {
        grid-template-columns: 1fr;
    }

    .gen-grid {
        grid-template-columns: 1fr;
    }
}
</style>
